<template>
  <q-btn
    ref="buttonRef"
    v-if="isButtonVisible"
    :color="buttonBgColor"
    :text-color="buttonTextColor"
    :round="isRound"
    :disable="disable"
    :outline="outline"
    :class="classList"
    :size="`${size}px`"
    :style="style"
    :flat="flat"
    class="nowrap"
  >
    <template v-if="slots.default?.()">
      <slot />
    </template>
    <template v-else>
      <template v-if="avatar?.length">
        <q-avatar :size="`${avatarSize}px`">
          <img
            v-if="!avatarImgError"
            @error="avatarImgError = true"
            :alt="avatar"
            :src="avatar"
          />
          <ml-element-icon v-else icon="question_mark"></ml-element-icon>
        </q-avatar>
      </template>
      <template v-else>
        <span v-if="label && iconPosition !== EnumIconPosition.Left">{{
          label
        }}</span>
        <ml-element-icon
          v-if="icon"
          :position="label ? iconPosition : undefined"
          :icon="icon"
          :color="iconColor"
          :size="iconSizeCalculated"
        />
        <span v-if="label && iconPosition !== EnumIconPosition.Right">{{
          label
        }}</span>
      </template>
    </template>
  </q-btn>
</template>
<script setup lang="ts">
import MlElementIcon from 'components/elements/icons/MlElementIcon.vue'
import { EnumIconPosition } from 'src/models/components/enums/Positions'
import { computed, ref, StyleValue, useSlots } from 'vue'
import { QBtn } from 'quasar'

const defaultBtnSize = 14
const defaultIconSize = 16

const slots = useSlots()

const props = withDefaults(
  defineProps<{
    avatar?: string
    label?: string
    round?: boolean
    stack?: boolean
    bgColor?: string
    textColor?: string
    fullWidth?: boolean
    size?: number
    icon?: string
    iconSize?: number
    iconColor?: string
    iconPosition?: EnumIconPosition
    disable?: boolean
    style?: StyleValue
    outline?: boolean
    flat?: boolean
    bold?: boolean
    muted?: boolean
    nav?: boolean
  }>(),
  {
    bgColor: 'primary',
    textColor: 'on-primary',
    iconPosition: EnumIconPosition.Right,
    size: defaultBtnSize,
    iconSize: defaultIconSize,
    muted: false,
    nav: false,
  },
)

const buttonRef = ref<InstanceType<typeof QBtn> | null>(null)

const isButtonVisible = computed(
  () => !!(props.avatar?.length || props.label?.length || props.icon?.length),
)

const avatarSize = computed(() => {
  const calc = props.size - defaultBtnSize
  return 42 + (calc > 0 ? calc : 0) * 3
})

const iconSizeCalculated = computed(() => {
  if (props.iconSize !== defaultIconSize) {
    return props.iconSize
  }
  return props.size !== defaultBtnSize ? props.size * 1.5 : defaultIconSize
})

const avatarImgError = ref(false)

const isRound = computed(
  () => !!((props.round && !props.label?.length) || props.avatar?.length),
)
const classList = computed(() => {
  let classes = ''
  classes += props.fullWidth ? 'full-width' : ''
  classes += props.stack ? ' ml-stack-button' : ''
  classes += props.bold ? ' text-weight-medium' : ' text-weight-regular'
  return classes
})

const buttonBgColor = computed<string>(() => {
  let customColor = props.bgColor
  if (buttonRef?.value) {
    const style = buttonRef.value?.$el?.style
    if (style.backgroundColor?.length) {
      customColor = style.backgroundColor
    }
  }
  if (props.muted) customColor = 'on-secondary-muted'
  if (props.nav && props.outline) customColor = 'on-accent-muted'
  return customColor
})

const buttonTextColor = computed(() =>
  props.outline ? buttonBgColor.value : props.textColor,
)
</script>
